import useTranslator from "../../hooks/useTranslator";
import { getUserDisplayName } from "../../helper/common";

const TenantCard = ({ tenant, requestedAt, currentLang }) => {
  const { _t } = useTranslator();

  const formatDate = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? ""
      : new Intl.DateTimeFormat(currentLang, {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(date);
  };

  const outdatedSince = formatDate(
    tenant?.profile_renewed_at ||
      tenant?.profile_completed_at ||
      tenant?.created_at
  );

  const tenantName =
    getUserDisplayName(tenant) ??
    tenant?.forensic_name ??
    tenant?.fname ??
    _t("tenant");
  const tenantInitials = tenantName
    .split(" ")
    .filter(Boolean)
    .map((name) => name.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");

  return (
    <div className="profile-update-tenant-card">
      <span className="profile-update-tenant-avatar">
        {tenantInitials || "U"}
      </span>
      <div className="profile-update-tenant-info">
        <strong>{tenantName}</strong>
        <span>
          {requestedAt
            ? _t("profileExpiredOn", "Expired")
            : _t("profileOutdatedSince", "Outdated since")}{" "}
          {outdatedSince}
        </span>
      </div>
      <span
        className={`profile-update-status-badge ${
          requestedAt ? "requested" : ""
        }`}
      >
        {requestedAt
          ? _t("profileUpdateRequested", "Update requested")
          : _t("outdated", "Outdated")}
      </span>
    </div>
  );
};

const ProfileUpdateRequestModal = ({
  isOpen,
  tenant,
  tenants,
  requestedAt,
  currentLang,
  isSending,
  onClose,
  onSend,
}) => {
  const { _t } = useTranslator();

  if (!isOpen) return null;

  const allTenants = tenants || (tenant ? [tenant] : []);
  const isBulk = allTenants.length > 1;

  const formatDate = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? ""
      : new Intl.DateTimeFormat(currentLang, {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(date);
  };

  const primaryTenant = allTenants[0];
  const primaryTenantName = getUserDisplayName(primaryTenant || {});
  const primaryTenantFirstName =
    primaryTenant?.forensic_name || primaryTenant?.fname || primaryTenantName;
  const requestedOn = formatDate(requestedAt);

  return (
    <div className="profile-update-modal-overlay" onClick={onClose}>
      <div
        className="profile-update-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-update-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="profile-update-modal-header">
          <h2 id="profile-update-modal-title">
            {requestedAt
              ? _t("sendProfileUpdateReminderTitle", "Send a Reminder")
              : _t("requestProfileUpdateTitle", "Request Profile Update")}
          </h2>
          <p>
            {requestedAt
              ? _t("profileUpdateAlreadyRequested1", "You already asked ") +
                primaryTenantFirstName +
                _t("profileUpdateAlreadyRequested2", " to update on ") +
                requestedOn +
                "."
              : _t(
                  "profileUpdateValidityDescription",
                  "Profiles stay current for 3 months after each update."
                )}
          </p>
        </div>

        {allTenants.map((t) => (
          <TenantCard
            key={t._id}
            tenant={t}
            requestedAt={requestedAt}
            currentLang={currentLang}
          />
        ))}

        <div className="profile-update-next-steps">
          <h3>{_t("whatHappensNext", "What happens next")}</h3>
          <ol>
            <li>
              {requestedAt ? (
                <>
                  {primaryTenantFirstName}{" "}
                  {_t("tenantIsRemindedToUpdateProfile")}
                </>
              ) : (
                <>
                  {_t(
                    "tenantIsNotifiedToUpdateProfile",
                    "The tenant is notified to update their profile."
                  )}
                </>
              )}
            </li>
            <li>
              {_t(
                "profileUpdateAccessRenewedDescription",
                "Once updated, you get access for 3 more months to view the profile and generate reports."
              )}
            </li>
            <li>
              {_t(
                "profileReopeningChargeDescription",
                "You will be charged again when reopening the profile."
              )}
            </li>
          </ol>
        </div>

        <div className="profile-update-modal-actions">
          <button
            type="button"
            className="profile-update-back-button"
            onClick={onClose}
          >
            {_t("goBack", "Go Back")}
          </button>
          <button
            type="button"
            className="profile-update-submit-button"
            disabled={isSending}
            onClick={onSend}
          >
            {isSending
              ? `${_t("sending")}...`
              : requestedAt
                ? _t("sendProfileUpdateReminder", "Send reminder")
                : isBulk
                  ? _t("sendProfileUpdateRequest", "Send request") +
                    ` (${allTenants.length})`
                  : _t("sendProfileUpdateRequest", "Send request")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileUpdateRequestModal;
